import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CoverageCategory } from 'src/app/models/product/coverage-category';
import { CoverageCategoryEdit } from 'src/app/models/product/coverage-category-edit';
import { CoverageCategoryOws } from 'src/app/models/product/coverage-category-ows';
import { NewCoverageCategory } from 'src/app/models/product/new-coverage-category';
import { environment } from 'src/environments/environment';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json'
  })
};

@Injectable({
  providedIn: 'root'
})
export class CoverageService {

  private apiUrl = environment.apiUrl;

  /**
   * Tracks the ID of the most recently edited coverage.
   * view-plans uses this to detect when to refresh the search results.
   */
  private lastEditedCoverageId: number | null = null;

  /**
   * Caches the last countries list used in view-plans for Coverage search.
   * When user returns from editing, we re-apply the same search to get fresh data.
   */
  private lastSearchCountries: string[] | null = null;

  constructor(
    private httpClient: HttpClient, 
  ) {
  }

  /**
   * Returns the ID of the most recently edited coverage (if any).
   * Clears the value after retrieval (one-time check).
   */
  getAndClearLastEditedCoverageId(): number | null {
    const id = this.lastEditedCoverageId;
    this.lastEditedCoverageId = null;
    return id;
  }

  /**
   * Saves the countries list used in view-plans for Coverage search.
   * This allows automatic re-search when user returns from editing.
   */
  cacheSearchCountries(countries: string[]): void {
    this.lastSearchCountries = countries ? [...countries] : null;
  }

  /**
   * Returns the cached countries list (if any).
   */
  getCachedSearchCountries(): string[] | null {
    return this.lastSearchCountries;
  }

  getAllCoverages(): Observable<CoverageCategoryOws[]> {
    return this.httpClient.get(`${this.apiUrl}/api/v1/coverage-categories/search`)
      .pipe(
        map((response: CoverageCategoryOws[]) => {
          return response;
        })
      );
  }

  getCoveragesByCountry(countries: string[]): Observable<CoverageCategoryOws[]> {
    return this.httpClient.post<CoverageCategoryOws[]>(`${this.apiUrl}/api/v1/coverage-categories/search/countries`, countries, httpOptions)
      .pipe(
        map((response: CoverageCategoryOws[]) => {
          return response;
        })
      );
  }
  
  getSearchCoverageCategoryAndProductId(productId: number): Observable<CoverageCategory[]> {
    return this.httpClient.get<CoverageCategory[]>(`${this.apiUrl}/api/v1/coverage-categories/search/${productId}`)
      .pipe(
        map((response: CoverageCategory[]) => {
          return response;
        })
      );
  }

  createCoverage(newCoverageCategoryDto: NewCoverageCategory): Observable<NewCoverageCategory> {
    return this.httpClient.post<NewCoverageCategory>(`${this.apiUrl}/api/v1/coverage-categories`, newCoverageCategoryDto, httpOptions);
  }

  getSearchByProductId(productId: number): Observable<CoverageCategory[]> {
    return this.httpClient.get<CoverageCategory[]>(`${this.apiUrl}/api/v1/coverage-categories/product-id/${productId}`)
      .pipe(
        map((response: CoverageCategory[]) => {
          return response;
        })
      );
  }

  getSearchById(productId: number): Observable<CoverageCategoryEdit> {
    return this.httpClient.get<CoverageCategoryEdit>(`${this.apiUrl}/api/v1/coverage-categories/search/coverageid/${productId}`)
      .pipe(
        map((response: CoverageCategoryEdit) => {
          return response;
        })
      );
  }

  updateCoverage(coverage: CoverageCategoryEdit): Observable<CoverageCategory> {
    return this.httpClient.put<CoverageCategory>(`${this.apiUrl}/api/v1/coverage-categories`, coverage, httpOptions)
      .pipe(
        tap((_updatedCoverage) => {
          // Track that this coverage was just edited
          if (coverage && typeof coverage === 'object' && 'id' in coverage) {
            this.lastEditedCoverageId = (coverage as unknown as Record<string, unknown>).id as number | null;
          }
        })
      );
  }
  
}
